import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import ImageTile from 'Compass/profile-lab/components/ImageTile';
import { Photo } from 'Compass/profile-lab/types';
import { attachPhotoStatus, extractDateParts, getSelectionColors } from 'Compass/profile-lab/utils/assetFormatting';
import { getSelectionSets } from 'Compass/profile-lab/utils/selectionUtils';
import { fetchSignedUrlsForCurrentUser } from 'Compass/profile-lab/hooks/useSignedUrls';
import { ErrorMessage, LoadingMessage } from 'Compass/profile-lab/styles/common';
import {
  CalendarDayCount,
  CalendarDayEmpty,
  CalendarDayHeader,
  CalendarDayImagesWrapper,
  CalendarDayLink,
  CalendarMonthCell,
  CalendarMonthCellEmpty,
  CalendarMonthCount,
  CalendarMonthGrid,
  CalendarNav,
  CalendarPage,
  CalendarTable,
  CalendarYearBlock,
  CalendarYearTitle
} from 'Compass/profile-lab/styles/calendar';

interface YearMonthData {
  [year: number]: {
    [month: number]: {
      photo_count: number;
      grow_count: number;
      selected_count: number;
    };
  };
}

interface DayData {
  photo_count: number;
  grow_count: number;
  selected_count: number;
}

const MONTH_NAMES = ['', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const MONTH_FULL_NAMES = ['', 'January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const DAY_NAMES = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const generateCalendarGrid = (year: number, month: number): number[][] => {
  const firstDay = new Date(year, month - 1, 1);
  const lastDay = new Date(year, month, 0);
  const daysInMonth = lastDay.getDate();

  let dayOfWeek = firstDay.getDay();
  if (dayOfWeek === 0) dayOfWeek = 7;

  const weeks: number[][] = [];
  let currentWeek: number[] = new Array(dayOfWeek - 1).fill(0);

  for (let day = 1; day <= daysInMonth; day++) {
    currentWeek.push(day);
    if (currentWeek.length === 7) {
      weeks.push(currentWeek);
      currentWeek = [];
    }
  }

  if (currentWeek.length > 0) {
    while (currentWeek.length < 7) {
      currentWeek.push(0);
    }
    weeks.push(currentWeek);
  }

  return weeks;
};

const ProfileLabCalendarPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const year = searchParams.get('year') ? parseInt(searchParams.get('year')!) : null;
  const month = searchParams.get('month') ? parseInt(searchParams.get('month')!) : null;
  const day = searchParams.get('day') ? parseInt(searchParams.get('day')!) : null;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [yearsData, setYearsData] = useState<YearMonthData>({});
  const [calendarData, setCalendarData] = useState<number[][]>([]);
  const [daysData, setDaysData] = useState<{ [day: number]: DayData }>({});
  const [monthName, setMonthName] = useState('');
  const [assets, setAssets] = useState<Photo[]>([]);
  const [signedUrls, setSignedUrls] = useState<Record<number, string>>({});
  const [allDatesWithPhotos, setAllDatesWithPhotos] = useState<Array<{year: number, month: number, day: number}>>([]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const { db } = await import('../services/db');
        await db.init();
        const allPhotos = await db.getAllPhotos();
        const { selectedSet, growSet } = await getSelectionSets();

        const dateMap = new Map<string, { year: number; month: number; day: number }>();
        const photosByDate = new Map<string, Photo[]>();

        allPhotos.forEach(photo => {
          const dateParts = extractDateParts(photo.photo_date);
          const monthKey = `${dateParts.year}-${dateParts.month}`;
          const dayKey = `${dateParts.year}-${dateParts.month}-${dateParts.day}`;

          const dateKey = `${dateParts.year}-${dateParts.month}-${dateParts.day}`;
          if (!dateMap.has(dateKey)) {
            dateMap.set(dateKey, dateParts);
          }

          if (!photosByDate.has(monthKey)) {
            photosByDate.set(monthKey, []);
          }
          if (!photosByDate.has(dayKey)) {
            photosByDate.set(dayKey, []);
          }

          photosByDate.get(monthKey)!.push(photo);
          photosByDate.get(dayKey)!.push(photo);
        });

        const uniqueDates = Array.from(dateMap.values()).sort((a, b) => {
          if (a.year !== b.year) return a.year - b.year;
          if (a.month !== b.month) return a.month - b.month;
          return a.day - b.day;
        });
        setAllDatesWithPhotos(uniqueDates);

        if (year !== null && month !== null && day !== null) {
          const dayKey = `${year}-${month}-${day}`;
          const dayPhotos = photosByDate.get(dayKey) || [];
          const photosWithStatus = attachPhotoStatus(dayPhotos, selectedSet, growSet);
          setAssets(photosWithStatus);
          setMonthName(MONTH_FULL_NAMES[month]);
        } else {
          setAssets([]);
        }

        if (year === null || month === null) {
          const updatedYearsData: YearMonthData = {};

          photosByDate.forEach((photos, key) => {
            const parts = key.split('-');
            if (parts.length === 2) {
              const y = parseInt(parts[0]);
              const m = parseInt(parts[1]);

              if (!updatedYearsData[y]) {
                updatedYearsData[y] = {};
              }

              const growCount = photos.filter(p => growSet.has(p.photo_lid)).length;
              const selectedCount = photos.filter(p => selectedSet.has(p.photo_lid)).length;

              updatedYearsData[y][m] = {
                photo_count: photos.length,
                grow_count: growCount,
                selected_count: selectedCount,
              };
            }
          });

          setYearsData(updatedYearsData);
        } else if (day === null) {
          const updatedDaysData: { [day: number]: DayData } = {};

          photosByDate.forEach((photos, key) => {
            const parts = key.split('-');
            if (parts.length === 3) {
              const y = parseInt(parts[0]);
              const m = parseInt(parts[1]);
              const d = parseInt(parts[2]);

              if (y === year && m === month) {
                const growCount = photos.filter(p => growSet.has(p.photo_lid)).length;
                const selectedCount = photos.filter(p => selectedSet.has(p.photo_lid)).length;

                updatedDaysData[d] = {
                  photo_count: photos.length,
                  grow_count: growCount,
                  selected_count: selectedCount,
                };
              }
            }
          });

          setCalendarData(generateCalendarGrid(year, month));
          setDaysData(updatedDaysData);
          setMonthName(MONTH_FULL_NAMES[month]);
        }
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to load calendar data';
        setError(errorMessage);
        console.error('Failed to fetch calendar data:', err);
      } finally {
        setLoading(false);
      }
    };

    void fetchData();
  }, [year, month, day]);

  useEffect(() => {
    const loadSignedUrls = async () => {
      if (year === null || month === null || day === null) {
        setSignedUrls({});
        return;
      }

      if (assets.length === 0) {
        return;
      }

      try {
        const photoLids = assets.map(a => a.photo_lid);
        const urls = await fetchSignedUrlsForCurrentUser(photoLids);
        setSignedUrls(urls);
      } catch (error) {
        console.error('Failed to fetch signed URLs:', error);
      }
    };

    void loadSignedUrls();
  }, [year, month, day, assets]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (year && month && day === null) {
        const prevMonth = month > 1 ? month - 1 : 12;
        const prevYear = month > 1 ? year : year - 1;
        const nextMonth = month < 12 ? month + 1 : 1;
        const nextYear = month < 12 ? year : year + 1;

        if (e.key === 'ArrowLeft') {
          navigate(`/profile-lab/calendar?year=${prevYear}&month=${prevMonth}`);
        } else if (e.key === 'ArrowRight') {
          navigate(`/profile-lab/calendar?year=${nextYear}&month=${nextMonth}`);
        }
      } else if (year && month && day !== null) {
        const currentIndex = allDatesWithPhotos.findIndex(
          d => d.year === year && d.month === month && d.day === day
        );

        if (currentIndex !== -1) {
          if (e.key === 'ArrowLeft' && currentIndex > 0) {
            const prevDate = allDatesWithPhotos[currentIndex - 1];
            navigate(`/profile-lab/calendar?year=${prevDate.year}&month=${prevDate.month}&day=${prevDate.day}`);
          } else if (e.key === 'ArrowRight' && currentIndex < allDatesWithPhotos.length - 1) {
            const nextDate = allDatesWithPhotos[currentIndex + 1];
            navigate(`/profile-lab/calendar?year=${nextDate.year}&month=${nextDate.month}&day=${nextDate.day}`);
          }
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [year, month, day, allDatesWithPhotos, navigate]);

  const currentIndex = useMemo(() => {
    return allDatesWithPhotos.findIndex(
      d => d.year === year && d.month === month && d.day === day
    );
  }, [allDatesWithPhotos, year, month, day]);

  const prevDate = currentIndex > 0 ? allDatesWithPhotos[currentIndex - 1] : null;
  const nextDate = currentIndex < allDatesWithPhotos.length - 1 ? allDatesWithPhotos[currentIndex + 1] : null;

  if (loading) {
    return <LoadingMessage>Loading...</LoadingMessage>;
  }

  if (error) {
    return <ErrorMessage>Error: {error}</ErrorMessage>;
  }

  // Year/Month overview
  if (year === null || month === null) {
    const sortedYears = Object.keys(yearsData).map(Number).sort((a, b) => b - a);

    return (
      <CalendarPage>
        {sortedYears.map((y) => (
          <CalendarYearBlock key={y}>
            <CalendarYearTitle>{y}</CalendarYearTitle>
            <CalendarMonthGrid>
              {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((m) => {
                const monthData = yearsData[y]?.[m];
                if (monthData) {
                  const colors = getSelectionColors(
                    monthData.grow_count,
                    monthData.selected_count,
                    monthData.photo_count
                  );
                  return (
                    <CalendarMonthCell
                      key={m}
                      to={`/profile-lab/calendar?year=${y}&month=${m}`}
                      style={{
                        backgroundColor: colors.backgroundColor,
                        color: colors.color,
                      }}
                    >
                      {MONTH_NAMES[m]}
                      <br />
                      <CalendarMonthCount>
                        ({monthData.grow_count}/{monthData.selected_count}/{monthData.photo_count})
                      </CalendarMonthCount>
                    </CalendarMonthCell>
                  );
                } else {
                  return (
                    <CalendarMonthCellEmpty key={m}>
                      {MONTH_NAMES[m]}
                      <br />
                      <CalendarMonthCount>(0/0/0)</CalendarMonthCount>
                    </CalendarMonthCellEmpty>
                  );
                }
              })}
            </CalendarMonthGrid>
          </CalendarYearBlock>
        ))}
      </CalendarPage>
    );
  }

  // Month calendar view
  if (day === null) {
    const prevMonth = month > 1 ? month - 1 : 12;
    const prevYear = month > 1 ? year : year - 1;
    const nextMonth = month < 12 ? month + 1 : 1;
    const nextYear = month < 12 ? year : year + 1;

    return (
      <CalendarPage>
        <CalendarNav>
          <Link to="/profile-lab/calendar">← Back</Link>
          {' | '}
          <Link to={`/profile-lab/calendar?year=${prevYear}&month=${prevMonth}`}>
            ← {MONTH_FULL_NAMES[prevMonth]}
          </Link>
          {' | '}
          <Link to={`/profile-lab/calendar?year=${nextYear}&month=${nextMonth}`}>
            {MONTH_FULL_NAMES[nextMonth]} →
          </Link>
        </CalendarNav>
        <h2>{monthName} {year}</h2>
        <CalendarTable>
          <thead>
            <tr>
              {DAY_NAMES.map((dayName) => (
                <th key={dayName}>
                  {dayName}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {calendarData.map((week, weekIdx) => (
              <tr key={weekIdx}>
                {week.map((dayNum, dayIdx) => {
                  if (dayNum === 0) {
                    return (
                      <td key={dayIdx} />
                    );
                  }

                  const dayData = daysData[dayNum];
                  if (dayData) {
                    const colors = getSelectionColors(
                      dayData.grow_count,
                      dayData.selected_count,
                      dayData.photo_count
                    );
                    return (
                      <td
                        key={dayIdx}
                        style={{
                          backgroundColor: colors.backgroundColor,
                        }}
                      >
                        <CalendarDayLink
                          to={`/profile-lab/calendar?year=${year}&month=${month}&day=${dayNum}`}
                          style={{
                            color: colors.color,
                          }}
                        >
                          {dayNum}
                          <br />
                          <CalendarDayCount>
                            ({dayData.grow_count}/{dayData.selected_count}/{dayData.photo_count})
                          </CalendarDayCount>
                        </CalendarDayLink>
                      </td>
                    );
                  } else {
                    return (
                      <td key={dayIdx}>
                        <CalendarDayEmpty>
                          {dayNum}
                        </CalendarDayEmpty>
                      </td>
                    );
                  }
                })}
              </tr>
            ))}
          </tbody>
        </CalendarTable>
      </CalendarPage>
    );
  }

  // Day photos view
  const daySuffix = (d: number) => {
    if (d >= 11 && d <= 13) return 'th';
    const lastDigit = d % 10;
    if (lastDigit === 1) return 'st';
    if (lastDigit === 2) return 'nd';
    if (lastDigit === 3) return 'rd';
    return 'th';
  };

  return (
    <CalendarPage>
      <CalendarNav>
        <Link to={`/profile-lab/calendar?year=${year}&month=${month}`}>← Back</Link>
        {' | '}
        {prevDate ? (
          <Link to={`/profile-lab/calendar?year=${prevDate.year}&month=${prevDate.month}&day=${prevDate.day}`}>
            ← Previous day
          </Link>
        ) : (
          <span style={{ color: '#666' }}>← Previous day</span>
        )}
        {' | '}
        {nextDate ? (
          <Link to={`/profile-lab/calendar?year=${nextDate.year}&month=${nextDate.month}&day=${nextDate.day}`}>
            Next day →
          </Link>
        ) : (
          <span style={{ color: '#666' }}>Next day →</span>
        )}
      </CalendarNav>
      <CalendarDayHeader>{monthName} {day}{daySuffix(day)} {year}</CalendarDayHeader>
      <CalendarDayImagesWrapper>
        {assets.map((asset) => (
          <ImageTile key={asset.photo_lid} asset={asset} imageUrl={signedUrls[asset.photo_lid]} />
        ))}
      </CalendarDayImagesWrapper>
    </CalendarPage>
  );
};

export default ProfileLabCalendarPage;
